import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";

import { mapMultiAnalysisMeasureTypeToAnalytics } from "../utils/measure-type-map";
import { fetchMultiAnalysisMeasureData, fetchMultiAnalysisMeasureOptions } from "../utils/measure-options";
import { MULTI_ANALYSIS_MEASURE_TYPE, type IStructureTreePoint } from "../types";
import { normalizeTrendRangeForRequest } from "../utils/trend-series";
import {
  groupTemplateMeasureRows,
  type ITemplateMeasureMetadata,
  type ITemplateMeasureRow,
} from "../utils/template-measure-rows";

interface ILastMeasureGroup {
  name: string;
  data: Array<{
    idMeasure: number;
    idPoint: number;
    measureDate: string;
    units: string;
    rangeType?: number;
    presentation?: number;
    param1?: number;
    param2?: number;
    param3?: number;
  }>;
}

type TLastMeasuresResponse = Record<string, ILastMeasureGroup | undefined>;

interface IUseTemplateMeasureRowsArgs {
  enabled: boolean;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE;
  points: readonly IStructureTreePoint[];
  startTimestamp: string;
  endTimestamp: string;
}

export const useTemplateMeasureRows = ({
  enabled,
  measureType,
  points,
  startTimestamp,
  endTimestamp,
}: IUseTemplateMeasureRowsArgs): { rows: ITemplateMeasureRow[]; isLoading: boolean } => {
  const { formatMessage } = useIntl();
  const { dbTz } = useAsu();
  const [rows, setRows] = useState<ITemplateMeasureRow[]>([]);
  const pointIdsKey = useMemo(
    () =>
      points
        .map(point => point.id)
        .sort((left, right) => left - right)
        .join(","),
    [points],
  );
  const pointNameById = useMemo(() => new Map(points.map(point => [point.id, point.title])), [points]);
  const lastRequestKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);

  const { fn: loadRows, isLoading } = useApiCall(
    async (api, requestPointIdsKey: string): Promise<ITemplateMeasureRow[]> => {
      const pointIds = requestPointIdsKey
        .split(",")
        .filter(Boolean)
        .map(id => Number(id))
        .filter(Number.isFinite);

      if (!pointIds.length) {
        return [];
      }

      const analyticsMeasureType = mapMultiAnalysisMeasureTypeToAnalytics(measureType);
      const measureOptions = await fetchMultiAnalysisMeasureOptions(api, analyticsMeasureType, pointIds);

      if (measureType === MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND) {
        const polarMeasureOptions = measureOptions.filter(option => option.useValue2);
        const polarMeasureNames = Array.from(new Set(polarMeasureOptions.map(option => option.title))).filter(Boolean);

        if (!polarMeasureNames.length) {
          return [];
        }

        const requestRange = normalizeTrendRangeForRequest(
          {
            start: startTimestamp,
            end: endTimestamp,
          },
          dbTz,
        );

        if (!requestRange) {
          return [];
        }

        const optionsByTitle = new Map(polarMeasureOptions.map(option => [option.title, option]));
        const response = await fetchMultiAnalysisMeasureData(api, {
          measureType: analyticsMeasureType,
          pointIds,
          measureNames: polarMeasureNames,
          range: requestRange,
        });
        const metadata: ITemplateMeasureMetadata[] = [];

        Object.values(response ?? {}).forEach(measure => {
          if (!measure?.name || !Array.isArray(measure.data)) {
            return;
          }

          const measureOption = optionsByTitle.get(measure.name);
          const latestByPoint = new Map<number, (typeof measure.data)[number]>();

          measure.data.forEach(row => {
            const previous = latestByPoint.get(row.idPoint);
            const rowTimestamp = new Date(row.Date).getTime();
            const previousTimestamp = previous ? new Date(previous.Date).getTime() : Number.NEGATIVE_INFINITY;

            if (!previous || rowTimestamp > previousTimestamp) {
              latestByPoint.set(row.idPoint, row);
            }
          });

          latestByPoint.forEach(row => {
            metadata.push({
              name: measure.name,
              idPoint: row.idPoint,
              pointName:
                pointNameById.get(row.idPoint) ??
                formatMessage({ id: "pages.multiAnalysis.templateEditor.pointFallback" }, { id: row.idPoint }),
              rangeType: measureOption?.rangeType ?? null,
              units: Number.isFinite(Number(row.Units)) ? Number(row.Units) : null,
              presentation: null,
              param1: null,
              param2: null,
              param3: null,
            });
          });
        });

        return groupTemplateMeasureRows(metadata);
      }

      const measureNames = Array.from(new Set(measureOptions.map(option => option.title))).filter(Boolean);

      if (!measureNames.length) {
        return [];
      }

      const response = (await api.fetchJson({
        url: "/point-v2/latest-measures",
        method: "GET",
        data: {
          points: JSON.stringify(pointIds),
          measureNames: JSON.stringify(measureNames),
          startTimestamp,
          endTimestamp,
          measureType: analyticsMeasureType,
        },
      })) as TLastMeasuresResponse | null | undefined;

      const metadata: ITemplateMeasureMetadata[] = [];

      Object.values(response ?? {}).forEach(group => {
        if (!group?.name || !Array.isArray(group.data)) {
          return;
        }

        group.data.forEach(item => {
          metadata.push({
            name: group.name,
            idPoint: item.idPoint,
            pointName:
              pointNameById.get(item.idPoint) ??
              formatMessage({ id: "pages.multiAnalysis.templateEditor.pointFallback" }, { id: item.idPoint }),
            rangeType: item.rangeType ?? null,
            units: Number.isFinite(Number(item.units)) ? Number(item.units) : null,
            presentation: item.presentation ?? null,
            param1: item.param1 ?? null,
            param2: item.param2 ?? null,
            param3: item.param3 ?? null,
          });
        });
      });

      return groupTemplateMeasureRows(metadata);
    },
    { success: false, error: false },
  );
  const loadRowsRef = useRef(loadRows);

  useEffect(() => {
    loadRowsRef.current = loadRows;
  }, [loadRows]);

  useEffect(() => {
    if (!enabled || !pointIdsKey) {
      lastRequestKeyRef.current = null;
      setRows([]);
      return;
    }

    const requestKey = `${measureType}:${pointIdsKey}:${startTimestamp}:${endTimestamp}`;

    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    lastRequestKeyRef.current = requestKey;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    void loadRowsRef
      .current(undefined, pointIdsKey)
      .then(nextRows => {
        if (requestIdRef.current === requestId) {
          setRows(nextRows ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          lastRequestKeyRef.current = null;
          setRows([]);
        }
      });
  }, [enabled, endTimestamp, measureType, pointIdsKey, startTimestamp]);

  return { rows, isLoading };
};
